import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { 
  Bitcoin, 
  Users,
  Plus,
  Trash2,
  LogOut,
  ArrowLeft,
  Mail,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import './Cashiers.css';

const Cashiers = () => {
  const { user, logout } = useContext(AuthContext);
  const [cashiers, setCashiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'owner') {
      toast.error('Only business owners can manage cashiers');
      navigate('/dashboard');
      return;
    }
    fetchCashiers();
  }, [user]);

  const fetchCashiers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/cashiers`, { headers });
      setCashiers(response.data);
    } catch (error) {
      toast.error('Failed to fetch cashiers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setFormData({
      name: '',
      email: '',
      password: ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('All fields are required');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/cashiers`, formData, { headers });
      toast.success('Cashier created');
      handleCloseModal();
      fetchCashiers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create cashier');
    }
  };

  const handleDelete = async (cashierId) => {
    if (!window.confirm('Delete this cashier account?')) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(`${API}/cashiers/${cashierId}`, { headers });
      toast.success('Cashier deleted');
      fetchCashiers();
    } catch (error) {
      toast.error('Failed to delete cashier');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="cashiers-page">
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <Bitcoin size={32} />
              <span>Crypto Cashier</span>
            </div>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span className="user-name">{user?.business_name}</span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button className="btn btn-secondary" onClick={handleLogout}>
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="dashboard-container">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={18} /> Back to Dashboard
          </button>

          <div className="page-header">
            <Users size={32} />
            <h1>Cashier Management</h1>
            <p>Manage cashier accounts for your POS system</p>
          </div>

          <div className="cashiers-actions">
            <button className="btn btn-primary" onClick={handleOpenModal}>
              <Plus size={18} /> Add Cashier
            </button>
          </div>

          <div className="cashiers-list">
            {cashiers.length === 0 ? (
              <div className="empty-state">
                <Users size={48} color="rgba(228, 230, 235, 0.3)" />
                <p>No cashiers yet</p>
                <button className="btn btn-primary" onClick={handleOpenModal}>
                  <Plus size={18} /> Create First Cashier
                </button>
              </div>
            ) : (
              <div className="cashiers-grid">
                {cashiers.map((cashier) => (
                  <div key={cashier.id} className="cashier-card">
                    <div className="cashier-icon">
                      <User size={32} />
                    </div>
                    <div className="cashier-info">
                      <h3 className="cashier-name">{cashier.business_name}</h3>
                      <div className="cashier-email">
                        <Mail size={14} />
                        {cashier.email}
                      </div>
                      <div className="cashier-meta">
                        <span className="cashier-badge">Cashier</span>
                        <span className="cashier-date">
                          Added {new Date(cashier.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <button 
                      className="btn-icon danger"
                      onClick={() => handleDelete(cashier.id)}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Create Cashier Account</h2>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Cashier Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. John Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="cashier@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Minimum 6 characters"
                  minLength="6"
                  required
                />
              </div>

              <div className="info-box">
                <p>ℹ️ Cashier accounts can:</p>
                <ul>
                  <li>Access the POS system</li>
                  <li>Process transactions</li>
                  <li>View products (but not edit)</li>
                </ul>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Cashier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cashiers;
